import { Controller } from "@hotwired/stimulus";

/**
 * Addon Controller
 *
 * Responsibilities:
 * • Sync inline addon section switches/quantities with closed-card summaries
 * • Notify pricing controller when addon selections change
 */

export default class extends Controller {
  static targets = ["switchInput", "quantityInput"];

  connect() {
    CloseableGroup.init();

    this._onChangeOrInput = (e) => {
      if (e.target.matches(".switch-wrapper input[type=\"checkbox\"]")) {
        this.updateClosedSwitchFromInput(e.target);
      } else if (e.target.matches(".quantity-input")) {
        if (e.type === "change") this.enforceMax(e.target);
        this.updateClosedQuantityFromInput(e.target);
      }
    };

    document.addEventListener("change", this._onChangeOrInput);
    document.addEventListener("input", this._onChangeOrInput);

    this.initialSync();
  }

  disconnect() {
    document.removeEventListener("change", this._onChangeOrInput);
    document.removeEventListener("input", this._onChangeOrInput);
  }

  initialSync() {
    if (this.hasSwitchInputTarget) {
      this.switchInputTargets.forEach((input) => this.updateClosedSwitchFromInput(input, { notify: false }));
    }

    if (this.hasQuantityInputTarget) {
      this.quantityInputTargets.forEach((input) => this.updateClosedQuantityFromInput(input, { notify: false }));
    }
  }

  updateClosedSwitchFromInput(input, options = {}) {
    const key = input.name;
    const section = input.closest(".addons-section");
    if (!section) return;

    const closedCard = section.querySelector(`.group-closed-content .closed-card[data-setting="${key}"]`);
    if (closedCard) {
      closedCard.classList.remove("switch-on", "switch-off");
      closedCard.classList.add(input.checked ? "switch-on" : "switch-off");
    }

    const addonRow = input.closest(".addon-option");
    if (addonRow) {
      addonRow.classList.toggle("active", !!input.checked);
    }

    if (options.notify === false) return;
    this.dispatchSelectionChanged({ key, value: input.checked ? 1 : 0 });
  }

  updateClosedQuantityFromInput(input, options = {}) {
    const key = input.name;
    const value = input.value ?? "";
    const section = input.closest(".addons-section");
    if (!section) return;

    const targetSpan = section.querySelector(`#addon-${key}`);
    if (targetSpan) {
      targetSpan.textContent = value;
    }

    const closedCard = section.querySelector(`.group-closed-content .closed-card[data-setting="${key}"]`);
    if (closedCard) {
      closedCard.classList.toggle("active", (parseFloat(value) || 0) > 0);
    }

    const addonRow = input.closest(".addon-option");
    if (addonRow) {
      addonRow.classList.toggle("active", (parseFloat(value) || 0) > 0);
    }

    const quantity = parseInt(value, 10) || 0;

    if (options.notify === false) return;
    this.dispatchSelectionChanged({ key, value: quantity });
  }

  dispatchSelectionChanged(options = {}) {
    this.dispatch("selection-changed", {
      bubbles: true,
      detail: {
        key: options.key,
        value: options.value
      }
    });
  }

  enforceMax(input) {
    const max = parseInt(input.max, 10);
    const value = parseInt(input.value, 10) || 0;
    if (!Number.isFinite(max) || value <= max) return;

    input.value = max;
    document.getElementById("wg-toast-container")?.dispatchEvent(
      new CustomEvent("toast:show", {
        detail: { message: input.dataset.maxExceededMessage, type: "error", duration: 5000 }
      })
    );
  }
}
